import React, { useMemo } from 'react';
import FilterIcon from '@atlaskit/icon/core/migration/filter';
import { HeadingItem } from '@atlaskit/menu';
import { FILTERS_ITEM_TYPE } from '@atlassian/jira-favourite-change-provider/src/model/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { handleGinSPAClick } from '@atlassian/jira-navigation-apps-common/src/utils/gin-spa/index.tsx';
import { testIdConcat } from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import { globalIssueNavigatorRoute } from '@atlassian/jira-router-routes-legacy-global-issue-navigator-routes/src/globalIssueNavigatorRoute.tsx';
import { DEFAULT_LOAD_COUNT_FAV } from '../../../../common/constants.tsx';
import { FavoriteButton } from '../../../../common/ui/menu/favourite-button/main.tsx';
import { MenuItem } from '../../../../common/ui/menu/item/index.tsx';
import { MenuList, MenuListItem } from '../../../../common/ui/menu/list/index.tsx';
import { MenuItemViewAll } from '../../../../common/ui/menu/view-all/main.tsx';
import { useFilterNavItems as useItems } from '../../../../controllers/items/index.tsx';
import messages from './messages.tsx';
import type { FavoriteContentProps } from './types.tsx';

export const FavoriteContent = ({ testIdPrefix }: FavoriteContentProps) => {
	const { formatMessage } = useIntl();
	const { favoriteItems, changeFavorite: onChangeFavorite, changedItems } = useItems();

	const itemsToShow = useMemo(
		() => favoriteItems.slice(0, DEFAULT_LOAD_COUNT_FAV),
		[favoriteItems],
	);

	const testId = testIdConcat(testIdPrefix, 'items-starred');

	const filtersUrl = '/jira/filters?page=1&sortKey=favourite&sortOrder=DESC';

	return favoriteItems.length > 0 ? (
		<span data-testid={testId}>
			<HeadingItem testId={testIdConcat(testId, 'heading')}>
				{formatMessage(messages.starred)}
			</HeadingItem>
			<MenuList>
				{itemsToShow.map((item, idx) => {
					const { url, id, favourite: isFavourite, title } = item;
					const contextItem = changedItems[item.id.toString()];
					const isPending =
						contextItem && contextItem.pending === true ? contextItem.pending : false;

					const filterParams = { issueKey: '' };
					const filterQuery = { filter: String(id) };

					return (
						<MenuListItem key={id} ariaLabel={formatMessage(messages.starred)}>
							<MenuItem
								analytics={{
									actionSubjectId: 'filtersNavigationMenuItem',
									menuItemId: id,
									menuItemType: 'starred',
									position: idx,
									starred: Boolean(isFavourite),
								}}
								href={url}
								iconBefore={<FilterIcon spacing="spacious" label="" />}
								iconAfter={
									<FavoriteButton
										id={String(id)}
										isFavorite={Boolean(isFavourite)}
										favoriteItemName={title}
										isPending={isPending}
										menuItemType="starred"
										onChangeFavorite={onChangeFavorite}
										type={FILTERS_ITEM_TYPE}
									/>
								}
								params={filterParams}
								query={filterQuery}
								testId={testIdConcat(testId, `item-${idx}`)}
								to={globalIssueNavigatorRoute}
								onClick={handleGinSPAClick}
								aria-label={title}
							>
								{title}
							</MenuItem>
						</MenuListItem>
					);
				})}
			</MenuList>
			{favoriteItems.length >= DEFAULT_LOAD_COUNT_FAV && (
				<MenuItemViewAll
					analytics={{
						actionSubjectId: 'filtersNavigationMenuItem',
						menuItemId: 'viewAll',
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						position: DEFAULT_LOAD_COUNT_FAV as number,
					}}
					href={filtersUrl}
					testId={testIdConcat(testId, 'item-view-all')}
					to={filtersUrl}
				/>
			)}
		</span>
	) : null;
};
